import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import api from '../../api/axios';
import TaskDetailsDialog from './TaskDetailsDialog';

const emptyFilters = { search: '', status: '', priority: '', countryId: '', assignedToId: '' };

const TasksSection: React.FC = () => {
  const [tasks, setTasks] = useState<any[]>([]);
  const [countries, setCountries] = useState<any[]>([]);
  const [team, setTeam] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [filters, setFilters] = useState(emptyFilters);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<any | null>(null);
  const [form, setForm] = useState({ title: '', description: '', countryId: '', assignedToId: '', priority: 'NORMAL', dueAt: '' });

  useEffect(() => {
    Promise.all([
      api.get('/sales-admin/accessible-countries'),
      api.get('/sales-admin/users/team'),
    ]).then(([countryResponse, teamResponse]) => {
      setCountries(countryResponse.data);
      setTeam(teamResponse.data);
    }).catch((requestError) => setError(requestError?.response?.data?.message || 'Filtre seçenekleri yüklenemedi.'));
  }, []);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/sales-admin/tasks', {
        params: {
          page,
          pageSize: 20,
          ...Object.fromEntries(Object.entries(filters).filter(([, value]) => Boolean(value))),
        },
      });
      setTasks(response.data.items);
      setTotal(response.data.total);
      setTotalPages(response.data.totalPages);
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Görevler yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  const updateFilter = (name: keyof typeof emptyFilters, value: string) => {
    setPage(1);
    setFilters((current) => ({ ...current, [name]: value }));
  };

  const applySearch = () => updateFilter('search', searchText.trim());

  const clearFilters = () => {
    setSearchText('');
    setPage(1);
    setFilters(emptyFilters);
  };

  const save = async () => {
    try {
      await api.post('/sales-admin/tasks', {
        title: form.title,
        description: form.description || undefined,
        countryId: form.countryId || undefined,
        assignedToId: form.assignedToId,
        priority: form.priority,
        dueAt: form.dueAt ? new Date(form.dueAt).toISOString() : undefined,
      });
      setForm({ title: '', description: '', countryId: '', assignedToId: '', priority: 'NORMAL', dueAt: '' });
      if (page === 1) await loadTasks();
      else setPage(1);
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Görev eklenemedi.');
    }
  };

  const setStatus = async (id: string, status: string) => {
    try {
      await api.patch(`/sales-admin/tasks/${id}`, { status });
      await loadTasks();
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Görev güncellenemedi.');
    }
  };

  const addResult = async (title: string, content: string) => {
    if (!selectedTask) return;
    const response = await api.post(`/sales-admin/tasks/${selectedTask.id}/results`, { title: title || undefined, content });
    const updatedTask = { ...selectedTask, results: [...(selectedTask.results || []), response.data] };
    setSelectedTask(updatedTask);
    setTasks((current) => current.map((task) => task.id === updatedTask.id ? updatedTask : task));
  };

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error">{error}</Alert>}
      <Box>
        <Typography variant="h4" fontWeight={700}>Görevler</Typography>
        <Typography color="text.secondary">Toplantı ve saha çalışmalarından doğan takip işlerini yönetin.</Typography>
      </Box>

      <Paper elevation={0} sx={{ p: 3 }}>
        <Stack spacing={2}>
          <TextField label="Görev Başlığı" value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} />
          <TextField multiline label="Açıklama" value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <FormControl fullWidth><InputLabel>Ülke</InputLabel><Select label="Ülke" value={form.countryId} onChange={(event) => setForm({ ...form, countryId: event.target.value })}><MenuItem value=""><em>Ülke seçilmedi</em></MenuItem>{countries.map((country) => <MenuItem key={country.id} value={country.id}>{country.name}</MenuItem>)}</Select></FormControl>
            <FormControl fullWidth><InputLabel>Atanan</InputLabel><Select label="Atanan" value={form.assignedToId} onChange={(event) => setForm({ ...form, assignedToId: event.target.value })}>{team.map((member) => <MenuItem key={member.id} value={member.id}>{member.name}</MenuItem>)}</Select></FormControl>
            <TextField fullWidth type="datetime-local" label="Son Tarih" InputLabelProps={{ shrink: true }} value={form.dueAt} onChange={(event) => setForm({ ...form, dueAt: event.target.value })} />
            <Button variant="contained" disabled={!form.title || !form.assignedToId} onClick={save}>Ekle</Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5}>
          <TextField
            size="small"
            label="Başlık veya açıklama ara"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') applySearch(); }}
            sx={{ minWidth: 240, flex: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}><InputLabel>Durum</InputLabel><Select label="Durum" value={filters.status} onChange={(event) => updateFilter('status', event.target.value)}><MenuItem value="">Tümü</MenuItem><MenuItem value="TODO">Bekliyor</MenuItem><MenuItem value="IN_PROGRESS">Devam Ediyor</MenuItem><MenuItem value="DONE">Tamamlandı</MenuItem><MenuItem value="CANCELLED">İptal</MenuItem></Select></FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}><InputLabel>Öncelik</InputLabel><Select label="Öncelik" value={filters.priority} onChange={(event) => updateFilter('priority', event.target.value)}><MenuItem value="">Tümü</MenuItem><MenuItem value="LOW">Düşük</MenuItem><MenuItem value="NORMAL">Normal</MenuItem><MenuItem value="HIGH">Yüksek</MenuItem><MenuItem value="URGENT">Acil</MenuItem></Select></FormControl>
          <FormControl size="small" sx={{ minWidth: 170 }}><InputLabel>Ülke</InputLabel><Select label="Ülke" value={filters.countryId} onChange={(event) => updateFilter('countryId', event.target.value)}><MenuItem value="">Tümü</MenuItem>{countries.map((country) => <MenuItem key={country.id} value={country.id}>{country.name}</MenuItem>)}</Select></FormControl>
          <FormControl size="small" sx={{ minWidth: 170 }}><InputLabel>Atanan</InputLabel><Select label="Atanan" value={filters.assignedToId} onChange={(event) => updateFilter('assignedToId', event.target.value)}><MenuItem value="">Tümü</MenuItem>{team.map((member) => <MenuItem key={member.id} value={member.id}>{member.name}</MenuItem>)}</Select></FormControl>
          <Button variant="contained" onClick={applySearch}>Ara</Button>
          <Button variant="text" onClick={clearFilters}>Temizle</Button>
        </Stack>
      </Paper>

      {loading ? (
        <Box sx={{ py: 7, textAlign: 'center' }}><CircularProgress /><Typography color="text.secondary" sx={{ mt: 1 }}>Görevler yükleniyor...</Typography></Box>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary">Toplam {total} görev · Sayfa başına 20 kayıt</Typography>
          {tasks.map((task) => (
            <Paper key={task.id} elevation={0} sx={{ p: 2, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'stretch', md: 'center' }, gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography fontWeight={700}>{task.title}</Typography>
                <Typography color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-wrap' }}>{task.description || 'Açıklama yok'}</Typography>
                {task.dueAt && <Typography variant="caption" color="text.secondary">Son tarih: {new Date(task.dueAt).toLocaleString('tr-TR')}</Typography>}
              </Box>
              <Button variant="outlined" size="small" onClick={() => setSelectedTask(task)}>Detay</Button>
              <Chip label={task.priority} />
              <Select size="small" value={task.status} onChange={(event) => setStatus(task.id, event.target.value)}><MenuItem value="TODO">Bekliyor</MenuItem><MenuItem value="IN_PROGRESS">Devam Ediyor</MenuItem><MenuItem value="DONE">Tamamlandı</MenuItem><MenuItem value="CANCELLED">İptal</MenuItem></Select>
            </Paper>
          ))}
          {!tasks.length && <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}><Typography color="text.secondary">Filtrelere uygun görev bulunamadı.</Typography></Paper>}
          {totalPages > 1 && <Pagination count={totalPages} page={page} onChange={(_, value) => setPage(value)} color="primary" sx={{ alignSelf: 'center' }} />}
        </>
      )}
      <TaskDetailsDialog
        task={selectedTask}
        countryName={countries.find((country) => country.id === selectedTask?.countryId)?.name}
        assignedToName={team.find((member) => member.id === selectedTask?.assignedToId)?.name}
        onAddResult={addResult}
        onClose={() => setSelectedTask(null)}
      />
    </Stack>
  );
};

export default TasksSection;
